import React, { useContext, useEffect } from "react";
import {
  Popover,
  IconButton,
  Typography,
  Box,
  Button,
  ButtonBase,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";
import { A11Y_ICONS } from "./a11yIcons";
import { useAccessibility } from "../../context/AccessibilityContext";
import { AppLayoutContext } from "../../../contexts/AppLayoutContext";
import { ThemeContext } from "../../../theme";

// Option set + per-tile glyphs are sourced verbatim from Figma node 89:17929
// (see a11yIcons.js). Order matches the design — 3 cols × 5 rows, last cell empty.
// See .agent/accessibility/README.md for per-option documentation.
const OPTIONS = A11Y_ICONS;

// Maps each Figma tile (by its label) to a behaviour:
//   • "toggle"     — flips one AccessibilityContext boolean (data-a11y-* attr)
//   • "saturation" — cycles off → low → gray
//   • "stepper"    — fires the App-level font setter (no on/off state)
//   • "theme"      — toggles light/dark colour mode via ThemeContext
//   • "soon"       — deferred (needs a font bundle / overlay);
//                    rendered disabled with a "Soon" marker until built.
// See .agent/accessibility/implementation-status.md for why each is where it is.
const BEHAVIOR = {
  "Bigger Text": { kind: "stepper", dir: "up" },
  "Smaller Text": { kind: "stepper", dir: "down" },
  "Text Spacing": { kind: "toggle", key: "textSpacing" },
  "Line Height": { kind: "toggle", key: "lineHeight" },
  "Dyslexia\nFriendly": { kind: "soon" },
  "ADHD Mode": { kind: "soon" },
  Saturation: { kind: "saturation" },
  "Light-Dark": { kind: "theme" },
  "Invert Colors": { kind: "toggle", key: "invert" },
  "Highlight Links": { kind: "toggle", key: "highlightLinks" },
  "Text To Speech": { kind: "soon" },
  Cursor: { kind: "toggle", key: "bigCursor" },
  "Pause\nAnimation": { kind: "toggle", key: "pauseMotion" },
  "Hide Images": { kind: "toggle", key: "hideImages" },
};

// Font-size setter clamps (App.js): step 0.5, range 14–18px on a 16px base.
const FONT_MIN = 14;
const FONT_MAX = 18;

export const RewampAccessibilityOptionsDialog = ({ open, onClose, anchorEl }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const a11y = theme.palette.accessibility;

  const { toggle, cycleSaturation, reset, isActive } = useAccessibility();
  const {
    increaseFontSize = () => {},
    decreaseFontSize = () => {},
    resetTextSize = () => {},
    fontSize = 16,
  } = useContext(AppLayoutContext) || {};
  const { ourTheme, toggleTheme = () => {} } = useContext(ThemeContext) || {};

  // Resolve a tile's interactive state from its behaviour descriptor.
  const resolveTile = (label) => {
    const b = BEHAVIOR[label] || { kind: "soon" };
    switch (b.kind) {
      case "toggle":
        return { active: isActive(b.key), onClick: () => toggle(b.key) };
      case "saturation":
        return { active: isActive("saturation"), onClick: cycleSaturation };
      case "theme":
        return { active: ourTheme === "dark", onClick: toggleTheme };
      case "stepper":
        return {
          active: false,
          isStepper: true,
          disabled: b.dir === "up" ? fontSize >= FONT_MAX : fontSize <= FONT_MIN,
          onClick: b.dir === "up" ? increaseFontSize : decreaseFontSize,
        };
      default: // "soon"
        return { active: false, soon: true, disabled: true, onClick: undefined };
    }
  };

  const handleReset = () => {
    reset(); // clears data-a11y-* attrs + localStorage
    resetTextSize(); // font-size back to 16px (App-owned)
    if (ourTheme === "dark") toggleTheme(); // colour mode back to light
  };

  // Ctrl+F2 toggle is owned by the parent (RewampAccessibilityBar).
  // This effect just suppresses the browser default when the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.ctrlKey && e.key === "F2") e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const focusRing = {
    "&:focus-visible": {
      outline: `3px solid ${theme.palette.accent.butterscotch}`,
      outlineOffset: "2px",
    },
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      // Drop down beneath the trigger, right edges aligned (the trigger sits at
      // the right of the top utility bar). Falls back to a centered popover if
      // no anchor is available (e.g. Ctrl+F2 before the bar has mounted).
      anchorReference={anchorEl ? "anchorEl" : "none"}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      marginThreshold={8}
      aria-labelledby="accessibility-dialog-title"
      slotProps={{
        paper: {
          sx: {
            width: "min(437px, calc(100vw - 16px))",
            borderRadius: "12px",
            overflow: "hidden",
            mt: 1,
            boxShadow: `0px 8px 24px ${alpha(theme.palette.common.black, 0.22)}`,
            ...(anchorEl
              ? {}
              : {
                  // Beats MUI Popover inline positioning when anchorReference="none".
                  top: "50% !important",
                  left: "50% !important",
                  transform: "translate(-50%, -50%) !important",
                }),
          },
        },
      }}
    >
      {/* ── Header — Kaitoke Green band ───────────────────────────── */}
      <Box
        component="header"
        sx={{
          bgcolor: "primary.dark",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.875,
          px: 2.25,
        }}
      >
        <Typography
          id="accessibility-dialog-title"
          component="h2"
          sx={{
            color: "common.white",
            fontWeight: 700,
            fontSize: "1rem",
            lineHeight: 1.2,
            m: 0,
          }}
        >
          {t("Accessibility options")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Ctrl+F2 chip */}
          <Box
            aria-hidden="true"
            sx={{
              bgcolor: alpha(theme.palette.common.white, 0.15),
              borderRadius: "20px",
              px: 1.125,
              py: 0.375,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: a11y.shortcutText,
                fontWeight: 600,
                fontSize: "0.75rem",
                lineHeight: 1,
              }}
            >
              Ctrl+F2
            </Typography>
          </Box>

          {/* Close */}
          <IconButton
            onClick={onClose}
            aria-label={t("Close accessibility options")}
            sx={{
              width: 28,
              height: 28,
              p: 0,
              color: "common.white",
              bgcolor: alpha(theme.palette.common.white, 0.2),
              borderRadius: "14px",
              "&:hover": { bgcolor: alpha(theme.palette.common.white, 0.3) },
              ...focusRing,
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* ── Body — option grid on Island Spice surface ────────────── */}
      <Box
        sx={{
          bgcolor: a11y.bodyBg,
          p: 2,
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            columnGap: 1.125,
            rowGap: 1.25,
          }}
        >
          {OPTIONS
            // "Coming soon" tiles (Dyslexia Friendly, ADHD Mode, Text To Speech)
            // are commented out of the panel until their font bundle / overlay
            // ships — they stay defined in BEHAVIOR above so re-enabling is a
            // one-line revert. See .agent/accessibility/implementation-status.md.
            .filter((opt) => (BEHAVIOR[opt.label] || {}).kind !== "soon")
            .map((opt) => {
            const { active, onClick, disabled, soon, isStepper } = resolveTile(
              opt.label
            );
            return (
              <ButtonBase
                key={opt.label}
                onClick={onClick}
                disabled={disabled}
                // Steppers are actions (no on/off state) so they omit aria-pressed;
                // toggles expose their real state for assistive tech.
                aria-pressed={isStepper || soon ? undefined : active}
                aria-label={`${t(opt.label.replace(/\n/g, " "))}${
                  soon ? ` (${t("coming soon")})` : ""
                }`}
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  // Active toggle gets the brand tint + ring; idle stays white.
                  bgcolor: active ? "primary.softSurface" : a11y.tileBg,
                  color: a11y.tileText,
                  borderRadius: "12px",
                  border: `1px solid ${active ? theme.palette.primary.interactive : "transparent"}`,
                  px: 1.5,
                  pt: 1.75,
                  pb: 1.5,
                  minHeight: 88,
                  height: "100%",
                  textAlign: "center",
                  opacity: soon ? 0.55 : 1,
                  transition: "border-color 0.15s ease, background-color 0.15s ease",
                  "&:hover": { borderColor: "primary.interactive" },
                  "&.Mui-disabled": {
                    opacity: soon ? 0.55 : 0.4,
                    cursor: "not-allowed",
                    pointerEvents: "auto", // keep cursor feedback on disabled steppers
                  },
                  ...focusRing,
                }}
              >
                {/* Exact Figma glyph rendered with currentColor so the fill
                    flows from the tile's `color` token (Figma's #11151E).
                    scaleY(-1) undoes the vertical mirroring in the raw export. */}
                <Box
                  component="svg"
                  aria-hidden="true"
                  focusable="false"
                  viewBox={opt.viewBox}
                  sx={{
                    height: 28,
                    width: "auto",
                    color: active ? "primary.interactiveHover" : a11y.tileText,
                    transform: "scaleY(-1)",
                  }}
                >
                  <path d={opt.path} fill="currentColor" />
                </Box>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: active ? 700 : 500,
                    fontSize: "0.75rem",
                    lineHeight: "0.93rem",
                    color: active ? "primary.interactiveHover" : a11y.tileText,
                    whiteSpace: "pre-line",
                  }}
                >
                  {t(opt.label)}
                </Typography>

                {soon && (
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      px: 0.625,
                      py: 0.125,
                      borderRadius: "8px",
                      bgcolor: a11y.resetBg,
                      color: a11y.resetText,
                      fontSize: "0.5625rem",
                      fontWeight: 700,
                      lineHeight: 1.4,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {t("Soon")}
                  </Box>
                )}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {/* ── Footer — Reset pill + UX4G credit ─────────────────────── */}
      <Box
        component="footer"
        sx={{
          bgcolor: "common.white",
          borderTop: `0.8px solid ${a11y.footerDivider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          px: 2,
        }}
      >
        <Button
          onClick={handleReset}
          startIcon={<ReplayIcon />}
          aria-label={t("Reset All Accessibility Options")}
          sx={{
            "& .MuiButton-startIcon > *": { fontSize: "1rem" },
            bgcolor: a11y.resetBg,
            color: a11y.resetText,
            border: `1px solid ${a11y.resetBorder}`,
            borderRadius: "24px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.75rem",
            lineHeight: 1,
            px: 1.875,
            py: 0.975,
            minHeight: 32,
            "&:hover": { bgcolor: alpha(a11y.resetBorder, 0.25) },
            ...focusRing,
          }}
        >
          {t("Reset All")}
        </Button>

        <Typography sx={{ color: a11y.metaText, fontSize: "0.75rem" }}>
          {t("Created by")}{" "}
          <Box component="span" sx={{ color: a11y.metaBrand, fontWeight: 700 }}>
            UX4G
          </Box>
        </Typography>
      </Box>
    </Popover>
  );
};

export default RewampAccessibilityOptionsDialog;
